import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getClientApprovals, respondToApproval, reopenApproval } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { Spinner } from './ClientDashboardPage';

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

const DECISION_SECTIONS = [
  {
    id: 'design',
    label: 'Design Review',
    types: ['look', 'designed_look'],
    empty: 'No design reviews are waiting.',
  },
  {
    id: 'product_pricing',
    label: 'Product + Pricing',
    types: ['campaign_tier', 'pricing'],
    empty: 'No product or pricing decisions are waiting.',
  },
  {
    id: 'launch',
    label: 'Launch Approval',
    types: ['timeline'],
    empty: 'No launch approvals are waiting.',
  },
  {
    id: 'production_fit',
    label: 'Production + Fit',
    types: ['fit_confirmation'],
    empty: 'No production or fit confirmations are waiting.',
  },
  {
    id: 'general',
    label: 'General',
    types: ['general'],
    empty: 'No general approvals are waiting.',
  },
  {
    id: 'history',
    label: 'History',
    types: [],
    empty: 'No approval history yet.',
  },
];

function statusConfig(status, T) {
  return {
    pending:        { label: 'Pending',        bg: 'rgba(201,168,76,0.12)',  color: T.gold, border: 'rgba(201,168,76,0.3)' },
    approved:       { label: 'Approved',       bg: 'rgba(34,197,94,0.1)',    color: T.id === 'light' ? '#15803d' : '#86efac', border: 'rgba(34,197,94,0.25)' },
    rejected:       { label: 'Rejected',       bg: 'rgba(239,68,68,0.1)',    color: T.id === 'light' ? '#b91c1c' : '#fca5a5', border: 'rgba(239,68,68,0.25)' },
    needs_revision: { label: 'Needs Revision', bg: 'rgba(99,102,241,0.1)',   color: T.id === 'light' ? '#4338ca' : '#a5b4fc', border: 'rgba(99,102,241,0.25)' },
  }[status] || { label: status || 'Unknown', bg: T.surface, color: T.textSub, border: T.border };
}

function sectionForApproval(approval) {
  const subjectType = String(approval?.subjectType || '');
  return DECISION_SECTIONS.find((section) => section.types.includes(subjectType))?.id || 'general';
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function humanSubjectType(value = '') {
  return String(value || 'general')
    .split('_')
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : '')
    .join(' ');
}

export default function ClientApprovalsPage() {
  const { theme: T } = useClientTheme();
  const [activeSection, setActiveSection] = useState('design');
  const [selectedId, setSelectedId] = useState('');
  const [noteById, setNoteById] = useState({});
  const [revisionReasonById, setRevisionReasonById] = useState({});
  const [submittingId, setSubmittingId] = useState('');
  const [pageError, setPageError] = useState('');

  const { data: pendingData, isLoading: lp, refetch: rp } = useQuery(['client-approvals', 'pending'], () => getClientApprovals('pending'));
  const { data: historyData, isLoading: lh, refetch: rh } = useQuery(['client-approvals', 'resolved'], () => getClientApprovals('resolved'));

  const pendingApprovals = useMemo(() => Array.isArray(pendingData?.approvals) ? pendingData.approvals : [], [pendingData]);
  const historyApprovals = useMemo(() => Array.isArray(historyData?.approvals) ? historyData.approvals : [], [historyData]);

  const counts = useMemo(() => {
    const out = Object.fromEntries(DECISION_SECTIONS.map((section) => [section.id, 0]));
    pendingApprovals.forEach((approval) => { out[sectionForApproval(approval)] += 1; });
    out.history = historyApprovals.length;
    return out;
  }, [pendingApprovals, historyApprovals]);

  useEffect(() => {
    if (activeSection === 'history' || counts[activeSection] > 0) return;
    const firstPendingSection = DECISION_SECTIONS.find((section) => section.id !== 'history' && counts[section.id] > 0);
    if (firstPendingSection) {
      setActiveSection(firstPendingSection.id);
      setSelectedId('');
    }
  }, [activeSection, counts]);

  const activeList = useMemo(() => {
    if (activeSection === 'history') return historyApprovals;
    return pendingApprovals.filter((approval) => sectionForApproval(approval) === activeSection);
  }, [activeSection, pendingApprovals, historyApprovals]);

  const selectedApproval = useMemo(() => (
    activeList.find((approval) => String(approval._id || approval.id) === selectedId) || activeList[0] || null
  ), [activeList, selectedId]);

  useEffect(() => {
    const currentId = selectedApproval ? String(selectedApproval._id || selectedApproval.id) : '';
    if (currentId && currentId !== selectedId) setSelectedId(currentId);
    if (!currentId && selectedId) setSelectedId('');
  }, [selectedApproval, selectedId]);

  const respond = async (aid, status) => {
    setPageError('');
    setSubmittingId(aid);
    try {
      await respondToApproval(aid, {
        status,
        clientNote: noteById[aid] || '',
        revisionReason: revisionReasonById[aid] || '',
      });
      await Promise.all([rp(), rh()]);
      setNoteById((prev) => ({ ...prev, [aid]: '' }));
      setRevisionReasonById((prev) => ({ ...prev, [aid]: '' }));
    } catch (err) {
      setPageError(err?.response?.data?.error || 'Failed to submit response.');
    } finally {
      setSubmittingId('');
    }
  };

  const reopen = async (aid) => {
    setPageError('');
    setSubmittingId(aid);
    try {
      await reopenApproval(aid);
      await Promise.all([rp(), rh()]);
      setActiveSection(sectionForApproval(selectedApproval));
    } catch (err) {
      setPageError(err?.response?.data?.error || 'Failed to send back for approval.');
    } finally {
      setSubmittingId('');
    }
  };

  const loading = lp || lh;
  const activeMeta = DECISION_SECTIONS.find((section) => section.id === activeSection) || DECISION_SECTIONS[0];

  return (
    <div>
      <style>{`
        @media (max-width: 980px) {
          .client-approval-workspace {
            grid-template-columns: 1fr !important;
          }
          .client-approval-nav {
            border-right: none !important;
            border-bottom: 1px solid var(--client-approval-border) !important;
            padding-right: 0 !important;
            padding-bottom: 12px !important;
          }
        }
      `}</style>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 750, color: T.text }}>Approvals</div>
        <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>
          Review brand decisions by area, with the active request separated from the queue.
        </div>
      </div>

      {pageError && (
        <div style={{ padding: '9px 12px', borderRadius: 8, border: `1px solid ${T.errorBorder}`, background: T.errorBg, color: T.errorText, fontSize: 12, marginBottom: 16 }}>
          {pageError}
        </div>
      )}

      {loading ? <Spinner T={T} /> : (
        <div
          className="client-approval-workspace"
          style={{ '--client-approval-border': T.border, display: 'grid', gridTemplateColumns: '190px minmax(230px, 0.65fr) minmax(520px, 1.8fr)', gap: 16, alignItems: 'start' }}
        >
          <nav className="client-approval-nav" style={{ borderRight: `1px solid ${T.border}`, paddingRight: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 10 }}>
              Decision Areas
            </div>
            {DECISION_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => { setActiveSection(section.id); setSelectedId(''); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 11px',
                  marginBottom: 4,
                  border: 'none',
                  borderLeft: activeSection === section.id ? `2px solid ${T.gold}` : '2px solid transparent',
                  background: activeSection === section.id ? (T.id === 'light' ? 'rgba(201,168,76,0.08)' : 'rgba(201,168,76,0.10)') : 'transparent',
                  color: activeSection === section.id ? T.text : T.textSub,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: activeSection === section.id ? 800 : 600,
                }}
              >
                <span>{section.label}</span>
                <span style={{ color: section.id === 'history' ? T.textMuted : T.gold, fontSize: 12 }}>{counts[section.id] || 0}</span>
              </button>
            ))}
          </nav>

          <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>{activeMeta.label}</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>{activeList.length} request{activeList.length === 1 ? '' : 's'}</div>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${T.border}` }}>
              {activeList.map((approval) => {
                const aid = String(approval._id || approval.id);
                const active = aid === String(selectedApproval?._id || selectedApproval?.id || '');
                const sc = statusConfig(approval.status, T);
                return (
                  <button
                    key={aid}
                    onClick={() => setSelectedId(aid)}
                    style={{
                      width: '100%',
                      display: 'block',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: `1px solid ${T.border}`,
                      background: active ? (T.id === 'light' ? 'rgba(15,23,42,0.035)' : 'rgba(255,255,255,0.045)') : 'transparent',
                      padding: '12px 8px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 850, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {approval.subjectLabel || humanSubjectType(approval.subjectType)}
                        </div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>
                          {humanSubjectType(approval.subjectType)} · {formatDate(approval.createdAt)}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 800, color: sc.color, whiteSpace: 'nowrap' }}>{sc.label}</span>
                    </div>
                  </button>
                );
              })}
              {!activeList.length ? (
                <div style={{ padding: '26px 8px', fontSize: 13, color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
                  {activeMeta.empty}
                </div>
              ) : null}
            </div>
          </section>

          <ApprovalDetail
            approval={selectedApproval}
            activeSection={activeSection}
            noteById={noteById}
            setNoteById={setNoteById}
            revisionReasonById={revisionReasonById}
            setRevisionReasonById={setRevisionReasonById}
            submittingId={submittingId}
            respond={respond}
            reopen={reopen}
            T={T}
          />
        </div>
      )}
    </div>
  );
}

function ApprovalDetail({ approval, activeSection, noteById, setNoteById, revisionReasonById, setRevisionReasonById, submittingId, respond, reopen, T }) {
  if (!approval) {
    return (
      <section style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ fontSize: 13, color: T.textMuted }}>Select a request to review.</div>
      </section>
    );
  }

  const aid = String(approval._id || approval.id);
  const busy = submittingId === aid;
  const sc = statusConfig(approval.status, T);
  const images = Array.isArray(approval.attachmentUrls) ? approval.attachmentUrls.filter(Boolean) : [];
  const snapshot = approval.snapshot || null;
  const isProductPricing = snapshot?.sourceType === 'campaign_product_pricing';

  return (
    <section style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>
              {humanSubjectType(approval.subjectType)}
            </div>
            <h2 style={{ margin: '5px 0 0', fontSize: 18, lineHeight: 1.25, color: T.text }}>
              {approval.subjectLabel || 'Approval request'}
            </h2>
          </div>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 999, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, flexShrink: 0 }}>
            {sc.label}
          </span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: T.textMuted }}>
          Requested {formatDate(approval.createdAt)}
        </div>
      </div>

      {images.length ? (
        <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ height: 360, background: T.id === 'light' ? '#eef1f5' : '#080808', border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }}>
            <img src={images[0]} alt={approval.subjectLabel || 'Approval attachment'} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          </div>
          {images.length > 1 ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, overflowX: 'auto' }}>
              {images.slice(1, 8).map((url) => (
                <div key={url} style={{ width: 58, height: 58, flex: '0 0 auto', border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden', background: T.id === 'light' ? '#eef1f5' : '#080808' }}>
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {isProductPricing ? (
        <ProductPricingSnapshot snapshot={snapshot} T={T} />
      ) : null}

      <div style={{ padding: 16, borderBottom: activeSection === 'history' ? 'none' : `1px solid ${T.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 8 }}>
          Studio Request
        </div>
        <div style={{ fontSize: 13, color: approval.studioNote ? T.textSub : T.textMuted, lineHeight: 1.55 }}>
          {approval.studioNote || 'No studio note was included.'}
        </div>
        {activeSection === 'history' && approval.clientNote ? (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 8 }}>
              Client Note
            </div>
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.55 }}>{approval.clientNote}</div>
            {approval.revisionReason ? (
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>
                Reason: {revisionReasonLabel(approval.revisionReason)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {activeSection !== 'history' ? (
        <div style={{ padding: 16 }}>
          {isProductPricing ? (
            <label style={{ display: 'block', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 6 }}>
                Revision Reason
              </div>
              <select
                value={revisionReasonById[aid] || ''}
                onChange={(event) => setRevisionReasonById((prev) => ({ ...prev, [aid]: event.target.value }))}
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', borderRadius: 7, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.inputColor, fontSize: 12, outline: 'none' }}
              >
                <option value="">Select only if requesting a revision</option>
                <option value="price">Price issue</option>
                <option value="product_list">Product list issue</option>
                <option value="image_copy">Image or copy issue</option>
                <option value="variant">Variant / size / colorway issue</option>
                <option value="payment_tax_shipping">Payment, tax, or shipping concern</option>
                <option value="other">Other</option>
              </select>
            </label>
          ) : null}
          <textarea
            placeholder="Optional note for Studio Sivad"
            value={noteById[aid] || ''}
            onChange={(event) => setNoteById((prev) => ({ ...prev, [aid]: event.target.value }))}
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 11px', borderRadius: 7, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.inputColor, fontSize: 13, resize: 'vertical', outline: 'none', marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button disabled={busy} onClick={() => respond(aid, 'approved')} style={actionButton(T, 'approve', busy)}>Approve</button>
            <button disabled={busy} onClick={() => respond(aid, 'needs_revision')} style={actionButton(T, 'revision', busy)}>Needs Revision</button>
            <button disabled={busy} onClick={() => respond(aid, 'rejected')} style={actionButton(T, 'reject', busy)}>Reject</button>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16, borderTop: `1px solid ${T.border}` }}>
          <button disabled={busy} onClick={() => reopen(aid)} style={actionButton(T, 'revision', busy)}>
            Send Back for Approval
          </button>
        </div>
      )}
    </section>
  );
}

function ProductPricingSnapshot({ snapshot, T }) {
  const products = Array.isArray(snapshot?.products) ? snapshot.products : [];
  const pricing = snapshot?.pricing || {};
  const summary = snapshot?.summary || {};
  const commerce = pricing.finalBalanceCommerce || {};

  return (
    <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
        <SnapshotMetric label="Campaign" value={snapshot?.campaignName || '-'} T={T} />
        <SnapshotMetric label="Products" value={summary.productCount || products.length || 0} T={T} />
        <SnapshotMetric label="Tiers" value={summary.tierCount || products.reduce((sum, p) => sum + (p.tiers?.length || 0), 0)} T={T} />
        <SnapshotMetric label="Price Range" value={priceRange(summary.priceRangeCents)} T={T} />
      </div>

      <section style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${T.border}`, fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>
          Payment Terms
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 0 }}>
          <SnapshotMetric label="Pricing Mode" value={humanSubjectType(pricing.pricingMode || 'standard_tiers')} T={T} flush />
          <SnapshotMetric label="Deposit" value={pricing.depositAmount ? formatMoney(Number(pricing.depositAmount || 0) * 100) : 'Full price at checkout'} T={T} flush />
          <SnapshotMetric label="Final Balance" value={pricing.finalBalanceMode === 'after_reservation_close' ? 'After preorder close' : 'None'} T={T} flush />
          <SnapshotMetric label="Stripe Tax" value={commerce.stripeTaxEnabled ? 'Enabled' : 'Not enabled'} T={T} flush />
          <SnapshotMetric label="Domestic Shipping" value={formatMoney(commerce.domesticShippingCents || 0)} T={T} flush />
          <SnapshotMetric label="International Shipping" value={formatMoney(commerce.internationalShippingCents || 0)} T={T} flush />
        </div>
      </section>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: T.text }}>
          <thead>
            <tr>
              {['Product', 'SKU', 'Material / Color', 'Tiers', 'Status'].map((heading) => (
                <th key={heading} style={{ textAlign: 'left', padding: '9px 10px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={`${product.productId}-${product.sku}-${product.title}`}>
                <td style={{ padding: 10, borderBottom: `1px solid ${T.border}`, minWidth: 190 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 46, height: 46, borderRadius: 6, border: `1px solid ${T.border}`, background: T.id === 'light' ? '#eef1f5' : '#080808', overflow: 'hidden', flex: '0 0 auto' }}>
                      {product.images?.[0] ? <img src={product.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} /> : null}
                    </div>
                    <div>
                      <div style={{ fontWeight: 850 }}>{product.title || 'Untitled product'}</div>
                      <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>{product.lookId || product.productId || '-'}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: 10, borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{product.sku || '-'}</td>
                <td style={{ padding: 10, borderBottom: `1px solid ${T.border}`, color: T.textSub }}>
                  <div>{product.material || '-'}</div>
                  <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>{product.colorway || ''}</div>
                </td>
                <td style={{ padding: 10, borderBottom: `1px solid ${T.border}`, minWidth: 210 }}>
                  {(product.tiers || []).length ? (
                    <div style={{ display: 'grid', gap: 5 }}>
                      {product.tiers.map((tier) => (
                        <div key={`${tier.tierId}-${tier.name}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <span style={{ color: T.textSub }}>{tier.name || 'Tier'}</span>
                          <strong>{formatMoney(tier.priceCents || 0)}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: T.textMuted }}>No tiers</span>
                  )}
                </td>
                <td style={{ padding: 10, borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{product.status || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SnapshotMetric({ label, value, T, flush = false }) {
  return (
    <div style={{ border: flush ? 'none' : `1px solid ${T.border}`, borderRight: flush ? `1px solid ${T.border}` : undefined, padding: flush ? 12 : 10, borderRadius: flush ? 0 : 8 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.label, fontWeight: 850 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.text, fontWeight: 800, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function priceRange(range = {}) {
  const min = Number(range.min || 0);
  const max = Number(range.max || 0);
  if (!min && !max) return '-';
  if (min === max) return formatMoney(min);
  return `${formatMoney(min)} - ${formatMoney(max)}`;
}

function revisionReasonLabel(value = '') {
  return {
    price: 'Price issue',
    product_list: 'Product list issue',
    image_copy: 'Image or copy issue',
    variant: 'Variant / size / colorway issue',
    payment_tax_shipping: 'Payment, tax, or shipping concern',
    other: 'Other',
  }[value] || value;
}

function actionButton(T, type, busy) {
  const styles = {
    approve: { border: '1px solid rgba(34,197,94,0.34)', background: 'rgba(34,197,94,0.1)', color: T.id === 'light' ? '#15803d' : '#86efac' },
    revision: { border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.textSub },
    reject: { border: '1px solid rgba(239,68,68,0.28)', background: 'rgba(239,68,68,0.1)', color: T.id === 'light' ? '#b91c1c' : '#fca5a5' },
  };
  return {
    ...styles[type],
    borderRadius: 7,
    padding: '9px 13px',
    fontSize: 12,
    fontWeight: 850,
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };
}
